import { Component, ComponentInterface, Element, Event, EventEmitter, h, Host, JSX, Listen, Method, Prop, State, Watch } from '@stencil/core';
import { observePropertySetter } from '../../global/observe-property-setter';
import { transformTagNames } from '../../global/transform-tag-name';
import { Keys } from '../../shared/keys';
import { DEFAULT_WORKBENCH_THEME, WorkbenchTheme } from '../../shared/themes';
import { querySelectorAllAsArray } from '../../utils/query-selector-all-as-array';
import { CheckboxState } from './checkbox-values';

const NUMBER_PLACEHOLDER_SCREENREADER = '$$$';

/**
 * @deprecated This component is deprecated and will be removed in the next major version. Please, use `wb-combobox` instead.
 * @slot - selectable options as wb-checkbox elements
 */
@Component({
  tag: 'wb-multi-select',
  styleUrl: 'multi-select.scss',
  shadow: true,
})
export class MultiSelect implements ComponentInterface {
  @Element()
  public el: HTMLElement;

  /** the color scheme */
  @Prop({ reflect: true })
  public theme: WorkbenchTheme = DEFAULT_WORKBENCH_THEME;

  /** disabled the component */
  @Prop({ reflect: true })
  public disabled: boolean = false;

  /** description of the multiselect overview */
  @Prop()
  public label: string;

  /**
   * label for screenreader to read "$$$ items selected"
   * $$$ will be replaced with the number of selected items
   */
  @Prop({ reflect: true })
  public screenreaderLabel: string = `selected ${NUMBER_PLACEHOLDER_SCREENREADER} items`;

  /** shows status of checkbox area */
  /** If present, shows status of checkbox area */
  @Prop({ reflect: true, mutable: true })
  public open = false;

  @State()
  public checkboxCount = 0;

  private overlayEl: HTMLElement;
  private buttonEl: HTMLElement;
  private checkboxEls: Array<HTMLWbCheckboxElement>;

  /** event emitted when checkbox value changes */
  @Event()
  public wbchange!: EventEmitter<Array<CheckboxState>>;

  @Listen('keydown', { target: 'document' })
  public onKeyDown(e: KeyboardEvent): void {
    if (this.open && e.key === Keys.ESC) {
      this.closeOverlay();
      this.buttonEl.focus();
    }
  }

  /** returns names of all checked checkboxes */
  @Method()
  public async getCheckedValues(): Promise<Array<string>> {
    return this.getCheckboxState()
      .filter((s) => s.checked)
      .map((s) => s.value);
  }

  /** returns all checkbox values */
  @Method()
  public async getValues(): Promise<Array<CheckboxState>> {
    return this.getCheckboxState();
  }

  /** resets all selected checkboxes */
  @Method()
  public async reset(): Promise<void> {
    this.checkboxEls.forEach((inputEl) => (inputEl.checked = false));
  }

  /** updates the counter displaying the number of selected checkboxes */
  @Method()
  public async updateCounter(): Promise<void> {
    this.updateCheckboxCounter(false);
  }

  @Watch('open')
  public onOpenChange(newValue: boolean, oldValue: boolean): void {
    const wasJustOpened = newValue && !oldValue;

    if (wasJustOpened) {
      // needs to run as separate task in event queue
      setTimeout(() => {
        document.addEventListener('click', this.closeOverlayWhenClickedOutside);
      });
    }
  }

  private closeOverlayWhenClickedOutside = (evt: MouseEvent) => {
    const isClickInsideOverlay = evt.composedPath().includes(this.overlayEl);

    if (!isClickInsideOverlay) {
      this.closeOverlay();
    }
  };

  private toggleOverlay = (): void => {
    this.open //
      ? this.closeOverlay()
      : this.openOverlay();
  };

  private toggleOverlayKeyDown = (event: KeyboardEvent): void => {
    if (event.code === 'Enter' || event.code === 'Space') {
      event.preventDefault();
      this.toggleOverlay();
    }
  };

  private openOverlay(): void {
    this.open = true;
    this.buttonEl.setAttribute('aria-expanded', 'true');
  }

  private closeOverlay(): void {
    this.open = false;
    this.buttonEl.setAttribute('aria-expanded', 'false');

    // remove any no longer needed click-outside-listeners
    document.removeEventListener('click', this.closeOverlayWhenClickedOutside);
  }

  private getCheckboxState(): Array<CheckboxState> {
    return this.checkboxEls.map((i) => ({ name: i.name, checked: i.checked, value: i.value }));
  }

  private updateCheckboxCounter(emitChangeEvent: boolean = true): void {
    const oldCheckboxCount = this.checkboxCount;

    this.checkboxCount = this.checkboxEls.filter((i) => i.checked).length;

    if (emitChangeEvent && this.checkboxCount !== oldCheckboxCount) {
      this.emitChangeEvent();
    }
  }

  private onResetClick = (event: MouseEvent): void => {
    this.reset();
    event.stopPropagation();
    this.closeOverlay();
    this.emitChangeEvent(); // reset() won't trigger wbchange event. must be done manually. fixes #2695
  };

  private TagNames = transformTagNames(this, 'wb-multi-select', ['wb-checkbox', 'wb-icon', 'wb-counter']);

  private emitChangeEvent(): void {
    this.wbchange.emit(this.getCheckboxState());
  }

  public connectedCallback(): void {
    this.updateListOfCheckboxEls();
    this.updateCheckboxCounter(false);
  }

  private get completeScreenreaderLabel(): string {
    // note: comma added to force a short pause in the screenreader
    return `${this.label}, ${this.screenreaderLabel.replace(NUMBER_PLACEHOLDER_SCREENREADER, `${this.checkboxCount}`)}`;
  }

  private setRef = (el: HTMLElement): void => {
    this.overlayEl = el;

    this.overlayEl.addEventListener('wbchange', (evt: CustomEvent) => {
      evt.stopPropagation(); // stop wbchange event from wb-checkbox elements from bubbling up
      this.updateCheckboxCounter();
    });
  };

  private setButtonRef = (el: HTMLElement): void => {
    this.buttonEl = el;
  };

  private updateListOfCheckboxEls = () => {
    this.checkboxEls = querySelectorAllAsArray<HTMLWbCheckboxElement>(this.el, this.TagNames.WbCheckbox);
    this.updateCounterOnProgramaticCheckedChanges();
  };

  private updateCounterOnProgramaticCheckedChanges(): void {
    this.updateCheckboxCounter(false); // Reset counter before checkbox counter is recalculated
    this.checkboxEls.forEach(async (checkboxEl) => {
      const nativeInput = await checkboxEl.getInputReference();
      // we need to check if the input already is checked when it comes in
      // TODO please refactor if someone has a better idea
      if (nativeInput.checked) {
        this.updateCounter();
      }
      observePropertySetter(nativeInput, 'checked', this.updateCounterArrowFn);
    });
  }

  private updateCounterArrowFn = () => {
    this.updateCounter();
  };

  // TODO: Refactor 2 nested buttons by replacing outer button
  // https://git.i.mercedes-benz.com/dh-io-workbench/core/issues/2849
  public render(): JSX.Element {
    const { WbCounter, WbIcon } = this.TagNames;

    return (
      <Host class="wb-multi-select">
        <div //
          class="button"
          aria-expanded={this.open}
          aria-label={this.completeScreenreaderLabel}
          tabindex={this.disabled ? '-1' : '0'}
          onKeyDown={this.toggleOverlayKeyDown}
          onClick={this.toggleOverlay}
          ref={this.setButtonRef}
        >
          <button
            type="button"
            tabindex="-1"
            aria-hidden="true"
            onClick={this.onResetClick}
            class={{
              close: true,
              'is-visible': this.checkboxCount > 0,
            }}
          >
            <WbIcon class="close-icon" name="bds/close/24" />
          </button>
          <div class="label">{this.label}</div>
          <WbCounter
            aria-hidden="true"
            value={this.checkboxCount}
            variant="tertiary"
            class={{
              counter: true,
              'counter--visible': this.checkboxCount > 0,
            }}
          />
          <WbIcon class="caret-icon" name="caret-down/16" />
        </div>
        <div class="overlay" aria-hidden={this.open ? 'false' : 'true'} ref={this.setRef}>
          <slot onSlotchange={this.updateListOfCheckboxEls} />
        </div>
      </Host>
    );
  }
}
